<script setup>
import { InformationCircleIcon } from "@heroicons/vue/24/solid";
import { ErrorMessage, Field } from "vee-validate";
import { computed, h, ref } from "vue";
import { useTippy } from "vue-tippy";

const model = defineModel();

const {
  fieldId,
  fieldName,
  label,
  placeholder = "",
  legend = null,
  type = "text",
  min = null,
  max = null,
  step = null,
  disabled = false,
  readonly = false,
  required = false,
  accept = "",
  multiple = false,
  tooltipIcon = InformationCircleIcon,
  tooltipContent = null,
  tooltipColor = null,
  tooltipPlacement = "right",
  inlineLabel = false,
  size = "medium",
} = defineProps({
  fieldId: {
    type: String,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  placeholder: {
    type: String,
    required: false,
  },
  legend: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: false,
  },
  min: {
    type: [Number, String],
    required: false,
  },
  max: {
    type: [Number, String],
    required: false,
  },
  step: {
    type: [Number, String],
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  readonly: {
    type: Boolean,
    required: false,
  },
  required: {
    type: Boolean,
    required: false,
  },
  accept: {
    type: String,
    required: false,
  },
  multiple: {
    type: Boolean,
    required: false,
  },
  tooltipIcon: {
    type: Function,
    required: false,
  },
  tooltipContent: {
    type: [String, Object],
    required: false,
  },
  tooltipColor: {
    type: String,
    required: false,
  },
  tooltipPlacement: {
    type: String,
    required: false,
  },
  size: {
    type: String,
    required: false,
  },
  inlineLabel: {
    type: Boolean,
    required: false,
  },
});

function focusInput() {
  document.querySelector("#" + fieldId).focus();
}

const tooltipButton = ref(null);

useTippy(tooltipButton, {
  animation: "scale",
  placement: tooltipPlacement,
  interactive: true,
  interactiveBorder: 10,
  content: computed(() => {
    if (
      tooltipContent !== null &&
      tooltipContent !== "" &&
      typeof tooltipContent !== "undefined"
    ) {
      if (typeof tooltipContent === "string") {
        return h("div", {
          class:
            "max-h-32 min-w-32 overflow-y-auto rounded bg-white px-2 py-1 drop-shadow wysiwyg",
          innerHTML: tooltipContent,
        });
      }

      return h(
        "div",
        {
          class:
            "max-h-32 min-w-32 overflow-y-auto rounded bg-white px-2 py-1 drop-shadow wysiwyg",
        },
        [tooltipContent],
      );
    }

    return h("div");
  }),
});
</script>

<template>
  <div
    class="mb-4 flex"
    :class="{
      hidden: type === 'hidden',
      'items-center': inlineLabel,
      'flex-col': !inlineLabel,
    }"
  >
    <label
      :for="fieldName"
      class="inline-flex items-center gap-1"
      :class="{
        'mr-2': inlineLabel,
        'mb-2': !inlineLabel,
        'text-sm': size === 'small',
      }"
    >
      {{ label }}

      <sup v-if="required" class="text-red-500">*</sup>

      <span
        v-if="
          tooltipContent !== null &&
          tooltipContent !== '' &&
          typeof tooltipContent !== 'undefined'
        "
        class="relative inline-flex items-center justify-center"
      >
        <button ref="tooltipButton" type="button">
          <component
            :is="tooltipIcon"
            class="size-5 cursor-help"
            :class="{
              'text-indigo-600': tooltipColor === null,
              'text-red-500': tooltipColor === 'red',
              'text-orange-500': tooltipColor === 'orange',
              'text-yellow-500': tooltipColor === 'yellow',
              'text-green-500': tooltipColor === 'green',
            }"
          />
        </button>
      </span>
    </label>

    <div class="flex">
      <div
        v-if="$slots.inputPrefix"
        class="h-[50px] whitespace-nowrap rounded rounded-r-none border border-r-0 border-gray-300 py-3 pl-5"
        @click="focusInput"
      >
        <slot name="inputPrefix"></slot>
      </div>

      <Field
        v-model="model"
        :id="fieldId"
        :name="fieldName"
        :type="type"
        :placeholder="placeholder"
        :min="min ? min : ''"
        :maxlength="max ? max : ''"
        :step="step ? step : ''"
        :disabled="disabled"
        :readonly="readonly"
        :accept="accept"
        :multiple="multiple"
        class="w-full rounded border-gray-300 ring-1 ring-transparent duration-150 file:mr-3 file:h-full file:cursor-pointer file:rounded file:border-0 file:bg-indigo-600 file:px-3 file:py-1 file:text-sm file:text-white file:shadow-none placeholder:text-gray-400 hover:file:bg-indigo-950 focus:outline-0 focus:ring-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100"
        :class="{
          '!h-[36px] cursor-pointer !p-0': type === 'file',
          border: type !== 'file',
          'rounded-l-none border-l-0 pl-1': $slots.inputPrefix,
          'h-[50px] px-5 py-3': size === 'medium',
          'h-[28px] px-3 py-1 text-sm': size === 'small',
        }"
      />
    </div>

    <em v-if="legend" class="mt-1 text-xs text-gray-400">
      {{ legend }}
    </em>

    <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
  </div>
</template>
